import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { invoicesService } from '@/services/invoicesService'
import { financeService } from '@/services/financeService'
import {
  CreditNote,
  CreditNoteLine,
  FinanceCustomer,
  TaxRate,
  CreditNoteListItem,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const CreditNotesDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const creditNoteFromList = (location.state as any)?.creditNote as CreditNoteListItem | undefined
  const branchId = user?.selectedBranch?.branchId
  const companyId = 0 // Placeholder until company context is available

  // Prefer 15% VAT; fall back to taxRateId 1, then first rate, then 15%
  const getDefaultTaxRate = (taxRates: TaxRate[]): { taxRateId: number; taxRate: number } => {
    const tax15 = taxRates.find((t) => {
      const rate = t.taxPercent || t.taxRate || 0
      return Math.abs(rate - 15) < 0.01
    })
    if (tax15) {
      return {
        taxRateId: tax15.taxRateId,
        taxRate: tax15.taxPercent || tax15.taxRate || 15,
      }
    }
    const taxById = taxRates.find((t) => t.taxRateId === 1)
    if (taxById) {
      return {
        taxRateId: taxById.taxRateId,
        taxRate: taxById.taxPercent || taxById.taxRate || 15,
      }
    }
    if (taxRates.length > 0) {
      return {
        taxRateId: taxRates[0].taxRateId,
        taxRate: taxRates[0].taxPercent || taxRates[0].taxRate || 15,
      }
    }
    return { taxRateId: 0, taxRate: 15 }
  }

  const [creditNote, setCreditNote] = useState<CreditNote>({
    creditNoteId: 0,
    creditNoteDate: calendarToday(),
    creditNoteNumber: '',
    customerId: 0,
    supplyDate: '',
    refInvNumber: '',
    refInvId: 0,
    currency: 0,
    conversionRate: 1,
    taxRateId: 0,
    subTotal: 0,
    totalTax: 0,
    totalDiscount: 0,
    total: 0,
    note: '',
    companyId: companyId,
    branchId: branchId || user?.selectedBranch?.branchId || 0,
    remainingAmount: 0,
    creditNoteType: 1,
    creditNoteLines: [],
  })

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<number>(0)
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  const invoiceLoadSeq = useRef(0)
  const linesLoadSeq = useRef(0)
  const [linesLoading, setLinesLoading] = useState(false)
  const [, setInvoiceLines] = useState<any[]>([])
  const [originalInvoiceLines, setOriginalInvoiceLines] = useState<Map<number, {
    qty: number
    unitPrice: number
    invoiceQty: number
    qtyEditable: boolean
    unitPriceEditable: boolean
    maxQty: number
    maxUnitPrice: number
    maxLineAmount: number
  }>>(new Map())

  const getInvoiceId = (inv: any): number => Number(inv?.invoiceId ?? inv?.id ?? 0)

  const mapInvoiceLineToCreditNote = (line: any): CreditNoteLine => {
    const originalQty = Number(line.originalQty ?? line.OriginalQty ?? line.qty ?? line.Qty ?? 0)
    const qtyEditable = line.qtyEditable ?? line.QtyEditable ?? originalQty > 1
    const taxRateId = Number(line.taxRateId ?? line.TaxRateId ?? 0)
    const taxFromLookup = taxRates.find((t) => t.taxRateId === taxRateId)
    const taxRate = Number(
      line.taxRate ?? line.TaxRate ?? line.taxPercent ?? taxFromLookup?.taxPercent ?? taxFromLookup?.taxRate ?? 0
    )
    const mapped: CreditNoteLine = {
      lineId: 0,
      invLineId: Number(line.invLineId || line.InvLineId || line.lineId || line.LineId || 0),
      creditNoteId: 0,
      itemId: line.itemId || line.ItemId || 0,
      description: line.description || line.Description || '',
      qty: Number(line.qty ?? line.Qty ?? 0),
      unitPrice: Number(line.unitPrice ?? line.UnitPrice ?? 0),
      itemPrice: Number(line.itemPrice ?? line.ItemPrice ?? 0),
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount: Number(line.taxableAmount ?? line.TaxableAmount ?? line.itemPrice ?? line.ItemPrice ?? 0),
      taxRateId,
      taxRate,
      taxPercent: taxRate,
      taxAmount: Number(line.taxAmount ?? line.TaxAmount ?? 0),
      lineAmount: Number(line.lineAmount ?? line.LineAmount ?? 0),
      companyId: companyId,
      branchId: branchId || 0,
      invoiceQty: originalQty,
      qtyEditable,
      unitPriceEditable: line.unitPriceEditable ?? line.UnitPriceEditable ?? originalQty <= 1,
      maxQty: Number(line.maxQty ?? line.MaxQty ?? line.qty ?? line.Qty ?? 0),
      maxUnitPrice: Number(line.maxUnitPrice ?? line.MaxUnitPrice ?? line.unitPrice ?? line.UnitPrice ?? 0),
      maxLineAmount: Number(line.maxLineAmount ?? line.MaxLineAmount ?? line.lineAmount ?? line.LineAmount ?? 0),
    }
    return mapped
  }

  const storeInvoiceLineLimits = (allLines: any[]) => {
    const originalValuesMap = new Map<number, {
      qty: number
      unitPrice: number
      invoiceQty: number
      qtyEditable: boolean
      unitPriceEditable: boolean
      maxQty: number
      maxUnitPrice: number
      maxLineAmount: number
    }>()
    allLines.forEach((line: any) => {
      const lineId = Number(line.invLineId || line.lineId || 0)
      if (lineId > 0) {
        const originalQty = Number(line.originalQty ?? line.qty ?? 0)
        const qtyEditable = line.qtyEditable ?? originalQty > 1
        originalValuesMap.set(lineId, {
          qty: Number(line.maxQty ?? line.qty ?? 0),
          unitPrice: Number(line.maxUnitPrice ?? line.unitPrice ?? 0),
          invoiceQty: originalQty,
          qtyEditable,
          unitPriceEditable: line.unitPriceEditable ?? !qtyEditable,
          maxQty: Number(line.maxQty ?? line.qty ?? 0),
          maxUnitPrice: Number(line.maxUnitPrice ?? line.unitPrice ?? 0),
          maxLineAmount: Number(line.maxLineAmount ?? line.lineAmount ?? 0),
        })
      }
    })
    setOriginalInvoiceLines(originalValuesMap)
    setInvoiceLines(allLines)
  }

  const applyInvoiceSourceLines = (sourceLines: any[], maxCreditAmount?: number) => {
    storeInvoiceLineLimits(sourceLines)
    setCreditNote((prev) => ({
      ...prev,
      creditNoteLines: sourceLines.map(mapInvoiceLineToCreditNote),
      remainingAmount:
        maxCreditAmount != null && Number.isFinite(Number(maxCreditAmount))
          ? Number(maxCreditAmount)
          : prev.remainingAmount,
    }))
  }

  const loadInvoiceSourceLines = useCallback(async (invoiceId: number, excludeId?: number) => {
    if (!branchId || !invoiceId) return
    const seq = ++linesLoadSeq.current
    setLinesLoading(true)
    try {
      const invoiceLines = await invoicesService.getInvoiceLinesById(branchId, invoiceId)
      if (seq !== linesLoadSeq.current) return

      let remainingLines: any[] = []
      let maxCreditAmount: number | undefined
      try {
        const remainingResult = await creditNotesService.getInvLines(invoiceId, branchId, excludeId)
        if (seq !== linesLoadSeq.current) return
        remainingLines = Array.isArray(remainingResult?.invoiceLines) ? remainingResult.invoiceLines : []
        maxCreditAmount = remainingResult?.maxCreditAmount
      } catch (error) {
        console.error('Failed to load credit note remaining lines', error)
      }

      const remainingById = new Map<number, any>()
      remainingLines.forEach((line: any) => {
        const id = Number(line.invLineId ?? line.lineId ?? line.LineId ?? 0)
        if (id > 0) remainingById.set(id, line)
      })

      let sourceLines = (invoiceLines || []).map((line: any) => {
        const lineId = Number(line.lineId ?? line.LineId ?? 0)
        const remaining = remainingById.get(lineId)
        if (remaining) return remaining
        const originalQty = Number(line.qty ?? line.Qty ?? 0) || (Number(line.lineAmount ?? line.LineAmount ?? 0) > 0 ? 1 : 0)
        const qtyEditable = originalQty > 1
        const taxRateId = Number(line.taxRateId ?? line.TaxRateId ?? 0)
        const taxFromLookup = taxRates.find((t) => t.taxRateId === taxRateId)
        const taxPercent = Number(line.taxRate ?? line.taxPercent ?? line.TaxRate ?? taxFromLookup?.taxPercent ?? taxFromLookup?.taxRate ?? 0)
        const qty = Number(line.qty ?? line.Qty ?? originalQty)
        const unitPrice = Number(line.unitPrice ?? line.UnitPrice ?? 0)
        const taxable = Number(line.taxableAmount ?? line.TaxableAmount ?? line.itemPrice ?? line.ItemPrice ?? qty * unitPrice)
        const taxAmount = Number(line.taxAmount ?? line.TaxAmount ?? taxable * (taxPercent / 100))
        const lineAmount = Number(line.lineAmount ?? line.LineAmount ?? taxable + taxAmount)
        return {
          ...line,
          invLineId: lineId,
          itemId: Number(line.itemId ?? line.ItemId ?? 0),
          description: line.description ?? line.Description ?? '',
          originalQty,
          qty: qty || 1,
          unitPrice,
          itemPrice: taxable,
          taxableAmount: taxable,
          taxRateId,
          taxRate: taxPercent,
          taxAmount,
          lineAmount,
          qtyEditable,
          unitPriceEditable: !qtyEditable,
          maxQty: qty || 1,
          maxUnitPrice: unitPrice,
          maxLineAmount: lineAmount,
        }
      })

      if (!sourceLines.length && remainingLines.length) {
        sourceLines = remainingLines
      }

      if (seq !== linesLoadSeq.current) return
      applyInvoiceSourceLines(sourceLines, maxCreditAmount)
    } catch (error) {
      console.error('Failed to load invoice lines for credit note', error)
      if (seq === linesLoadSeq.current) {
        setCreditNote((prev) => ({ ...prev, creditNoteLines: [] }))
      }
    } finally {
      if (seq === linesLoadSeq.current) {
        setLinesLoading(false)
      }
    }
  }, [branchId, taxRates, companyId])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deletedRowIndices] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (!branchId) {
      console.warn('BranchId is not available, cannot load data')
      // Call without await since useEffect callback is not async
      showValidationError('Please select a branch first')
      return
    }

    console.log('Loading lookups for branchId:', branchId)
    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, taxRatesData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          financeService.getTaxRates(branchId),
        ])

        console.log('Loaded data:', {
          customers: customersData?.length || 0,
          taxRates: taxRatesData?.length || 0,
        })

        setCustomers(customersData || [])
        setTaxRates(taxRatesData || [])
      } catch (error: any) {
        console.error('Failed to load credit note lookups', error)
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        })
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNotes'))
        
        // Set empty arrays to prevent undefined errors
        setCustomers([])
        setTaxRates([])
        setInvoices([])
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId, isEditMode])

  // Set default VAT (15%) for rows that don't have taxRateId when taxRates are loaded
  useEffect(() => {
    if (taxRates.length === 0) return
    
    const defaultTax = getDefaultTaxRate(taxRates)
    if (!defaultTax.taxRateId && taxRates.length === 0) {
      return
    }

    console.log('Setting default VAT:', defaultTax)

    setCreditNote(prev => {
      const hasRowsWithoutVAT = prev.creditNoteLines?.some(line => !line.taxRateId || line.taxRateId === 0)
      if (!hasRowsWithoutVAT) return prev

      const updatedLines = prev.creditNoteLines?.map(line => {
        // Only set default VAT if taxRateId is 0 or not set
        if (!line.taxRateId || line.taxRateId === 0) {
          console.log('Setting default VAT for line:', line, 'to taxRateId:', defaultTax.taxRateId)
          return {
            ...line,
            taxRateId: defaultTax.taxRateId,
            taxRate: defaultTax.taxRate,
            taxPercent: defaultTax.taxRate,
          }
        }
        return line
      }) || []

      return { ...prev, creditNoteLines: updatedLines }
    })
  }, [taxRates])

  // Load credit note data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return

    const loadCreditNote = async () => {
      try {
        setLoading(true)
        const creditNoteId = Number(id)
        
        console.log('Loading credit note data for edit mode:', { branchId, creditNoteId })
        
        // First, set data from list if available (for faster UI update)
        if (creditNoteFromList) {
          const customerId = creditNoteFromList.customerId || 0
          const customer = customers.find((c) => c.customerId === customerId)
          if (customer) {
            setSelectedCustomerId(customerId)
            if (customerId > 0 && branchId) {
              try {
                const invoicesData = await creditNotesService.getAllInvoices(branchId, customerId, undefined, creditNoteId)
                setInvoices(invoicesData || [])
              } catch (error) {
                console.error('Failed to load invoices for customer in edit mode', error)
              }
            }
          }
          
          setCreditNote((prev) => ({
            ...prev,
            creditNoteId: creditNoteFromList.creditNoteId || creditNoteFromList.id || creditNoteId,
            creditNoteNumber: creditNoteFromList.creditNoteNumber || creditNoteFromList.creditNoteNo || '',
            creditNoteDate: calendarFromApi(creditNoteFromList.creditNoteDate) || calendarToday(),
            customerId: customerId,
            supplyDate: calendarFromApi(creditNoteFromList.supplyDate) || '',
            refInvNumber: creditNoteFromList.refInvNumber || '',
            refInvId: creditNoteFromList.refInvId || 0,
            remainingAmount: creditNoteFromList.remainingAmount || 0,
            creditNoteType: 1,
            subTotal: creditNoteFromList.subTotal || 0,
            totalTax: creditNoteFromList.totalTax || 0,
            totalDiscount: creditNoteFromList.totalDiscount || 0,
            total: creditNoteFromList.total || 0,
            note: creditNoteFromList.note || '',
            branchId: branchId,
          }))
        }
        
        // Now load lines from API (this endpoint returns only lines)
        try {
          const creditNoteData = await creditNotesService.getCreditNoteById(branchId, creditNoteId)
          
          console.log('Loaded credit note lines from API:', creditNoteData.creditNoteLines?.length || 0)
          
          // Update with lines from API - filter out empty lines (only show lines with data)
          if (creditNoteData.creditNoteLines && creditNoteData.creditNoteLines.length > 0) {
            const validLines = creditNoteData.creditNoteLines.filter((line: CreditNoteLine) => {
              const qty = line.qty || 0
              if (qty <= 0) return false
              return !!(line.description?.trim() || (line.itemId && line.itemId > 0))
            })
            
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: validLines.length > 0 ? validLines : [],
              creditNoteType: 1,
              refInvId: creditNoteData.refInvId || prev.refInvId,
              customerId: creditNoteData.customerId || prev.customerId,
              subTotal: creditNoteData.subTotal ?? prev.subTotal,
              totalTax: creditNoteData.totalTax ?? prev.totalTax,
              totalDiscount: creditNoteData.totalDiscount ?? prev.totalDiscount,
              total: creditNoteData.total ?? prev.total,
            }))
            
            console.log('Credit note lines set in state:', validLines.length, 'valid lines out of', creditNoteData.creditNoteLines.length)
          } else {
            // If no lines, use empty array (not default rows) in edit mode
            setCreditNote((prev) => ({
              ...prev,
              creditNoteLines: [],
            }))
          }
          
          // If invoice is referenced, load invoice lines for reference
          const refInvId = creditNoteFromList?.refInvId || creditNoteData.refInvId
          if (refInvId) {
            try {
              const invLinesResponse = await creditNotesService.getInvLines(refInvId, branchId, creditNoteId)
              const allLines = invLinesResponse.invoiceLines || []
              storeInvoiceLineLimits(allLines)
              setCreditNote((prev) => ({
                ...prev,
                creditNoteLines: (prev.creditNoteLines || []).map((cnLine) => {
                  const source = allLines.find((l: any) => Number(l.invLineId || l.lineId) === Number(cnLine.invLineId))
                  if (!source) return cnLine
                  const originalQty = Number(source.originalQty ?? source.qty ?? cnLine.qty ?? 0)
                  const qtyEditable = source.qtyEditable ?? originalQty > 1
                  return {
                    ...cnLine,
                    invoiceQty: originalQty,
                    qtyEditable,
                    unitPriceEditable: source.unitPriceEditable ?? !qtyEditable,
                    maxQty: Number(source.maxQty ?? source.qty ?? cnLine.qty ?? 0),
                    maxUnitPrice: Number(source.maxUnitPrice ?? source.unitPrice ?? cnLine.unitPrice ?? 0),
                    maxLineAmount: Number(source.maxLineAmount ?? source.lineAmount ?? cnLine.lineAmount ?? 0),
                    taxRateId: cnLine.taxRateId || source.taxRateId,
                    taxRate: cnLine.taxRate || source.taxRate,
                    taxPercent: cnLine.taxPercent || source.taxRate,
                  }
                }),
              }))
            } catch (error) {
              console.warn('Failed to load invoice lines for reference', error)
            }
          }
        } catch (linesError: any) {
          console.error('Failed to load credit note lines', linesError)
          // Don't show error for lines if we have list data
          if (!creditNoteFromList) {
            await showError('Error', linesError.response?.data?.message || linesError.message || t('creditNotes.failedToLoadCreditNote'))
          }
          // Set empty array in edit mode if lines failed to load (don't show default rows)
          setCreditNote((prev) => ({
            ...prev,
            creditNoteLines: [],
          }))
        }
      } catch (error: any) {
        console.error('Failed to load credit note data', error)
        await showError('Error', error.response?.data?.message || error.message || t('creditNotes.failedToLoadCreditNote'))
      } finally {
        setLoading(false)
      }
    }

    // Wait for customers to load before loading credit note
    if (customers.length > 0 || !isEditMode) {
      loadCreditNote()
    }
  }, [isEditMode, branchId, id, customers, creditNoteFromList])

  // Lines are copied from the selected invoice. New rows cannot be added.

  const formatDateForInput = (dateString?: string): string => calendarFromApi(dateString)

  // handleCustomerChange removed - not currently used

  const calculateLineTotals = (line: CreditNoteLine): CreditNoteLine => {
    const qty = line.qty || 0
    const unitPrice = line.unitPrice || 0
    const taxPercent = line.taxPercent || line.taxRate || 0

    const itemPrice = qty * unitPrice
    const taxableAmount = itemPrice
    const taxAmount = taxableAmount * (taxPercent / 100)
    const lineAmount = taxableAmount + taxAmount

    return {
      ...line,
      itemPrice,
      discountPercent: 0,
      discountAmount: 0,
      taxableAmount,
      taxAmount,
      lineAmount,
    }
  }

  const summaryTotals = useMemo(() => {
    const validLines = creditNote.creditNoteLines?.filter((_, index) => !deletedRowIndices.has(index)) || []
    const subTotal = validLines.reduce((sum, line) => sum + (line.itemPrice || 0), 0)
    const totalDiscount = validLines.reduce((sum, line) => sum + (line.discountAmount || 0), 0)
    const taxableAmount = validLines.reduce((sum, line) => sum + (line.taxableAmount || 0), 0)
    const totalTax = validLines.reduce((sum, line) => sum + (line.taxAmount || 0), 0)
    const total = validLines.reduce((sum, line) => sum + (line.lineAmount || 0), 0)

    return {
      subTotal,
      totalDiscount,
      taxableAmount,
      totalTax,
      total,
    }
  }, [creditNote.creditNoteLines, deletedRowIndices])

  useEffect(() => {
    setCreditNote((prev) => ({
      ...prev,
      subTotal: summaryTotals.subTotal,
      totalDiscount: summaryTotals.totalDiscount,
      totalTax: summaryTotals.totalTax,
      total: summaryTotals.total,
    }))
  }, [summaryTotals])

  const isFormValid = useMemo(() => {
    if (!creditNote.customerId) return false
    if (!creditNote.refInvId) return false
    if (!creditNote.creditNoteLines || creditNote.creditNoteLines.length === 0) return false

    const validLines = creditNote.creditNoteLines.filter((line, index) => {
      const isDeleted = deletedRowIndices.has(index)
      if (isDeleted) return false
      if (!line.description || line.description.trim() === '') return false
      if (!line.qty || line.qty <= 0) return false
      if (!line.taxRateId || line.taxRateId <= 0) return false
      return true
    })

    return validLines.length > 0
  }, [creditNote.customerId, creditNote.refInvId, creditNote.creditNoteLines, deletedRowIndices])

  const handleLineChange = (index: number, field: keyof CreditNoteLine, value: any) => {
    setCreditNote((prev) => {
      const newLines = [...(prev.creditNoteLines || [])]
      const current = newLines[index]
      if (!current) return prev
      if (field === 'qty' && current.qtyEditable === false) return prev
      if (field === 'unitPrice' && current.unitPriceEditable === false) return prev
      if (field === 'taxRateId') return prev

      const updatedLine = { ...current, [field]: value }

      if (field === 'qty' && updatedLine.invLineId && updatedLine.invLineId > 0) {
        const maxQty = updatedLine.maxQty ?? originalInvoiceLines.get(updatedLine.invLineId)?.maxQty
        if (maxQty != null && Number(value) > maxQty) {
          showValidationError(`Quantity cannot exceed remaining quantity: ${maxQty}`)
          return prev
        }
      }

      if (field === 'unitPrice' && updatedLine.invLineId && updatedLine.invLineId > 0) {
        const maxUnitPrice = updatedLine.maxUnitPrice ?? originalInvoiceLines.get(updatedLine.invLineId)?.maxUnitPrice
        if (maxUnitPrice != null && Number(value) > maxUnitPrice + 0.009) {
          showValidationError(`Unit price cannot exceed remaining amount: ${maxUnitPrice.toFixed(2)}`)
          return prev
        }
      }

      newLines[index] = calculateLineTotals(updatedLine)

      const maxLineAmount = updatedLine.maxLineAmount ?? originalInvoiceLines.get(updatedLine.invLineId || 0)?.maxLineAmount
      if (maxLineAmount != null && maxLineAmount > 0 && (newLines[index].lineAmount || 0) > maxLineAmount + 0.009) {
        showValidationError(`Line amount cannot exceed remaining amount: ${maxLineAmount.toFixed(2)}`)
        return prev
      }

      const remainingAmount = prev.remainingAmount ?? 0
      if (remainingAmount > 0) {
        const totalLinesAmount = newLines
          .filter((_, idx) => !deletedRowIndices.has(idx))
          .reduce((sum, line) => sum + (line.lineAmount || 0), 0)
        if (totalLinesAmount > remainingAmount) {
          showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
          return prev
        }
      }

      return { ...prev, creditNoteLines: newLines }
    })
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      // Try to get IP from a service
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('creditNotes.pleaseSelectBranchFirst'))
      return
    }

    if (!isFormValid) {
      await showValidationError(t('creditNotes.pleaseFillAllRequiredFields'))
      return
    }

    const remainingAmount = creditNote.remainingAmount ?? 0
    if (remainingAmount <= 0) {
      await showValidationError(t('creditNotes.noRemainingForType'))
      return
    }
    if (remainingAmount > 0) {
      const totalLinesAmount = creditNote.creditNoteLines
        ?.filter((_, index) => !deletedRowIndices.has(index))
        .reduce((sum, line) => sum + (line.lineAmount || 0), 0) || 0
      if (totalLinesAmount > remainingAmount) {
        await showValidationError(t('creditNotes.amountCannotExceed', { total: totalLinesAmount.toFixed(2), remaining: remainingAmount.toFixed(2) }))
        return
      }
    }

    try {
      setSaving(true)
      
      // Show loading
      showLoading(
        isEditMode ? 'Updating Credit Note...' : 'Saving Credit Note...',
        'Please wait while we process your request'
      )
      
      // Get user IP address (logged-in user's IP, not server's IP)
      const ipAddress = await getUserIPAddress()
      
      // Filter out empty lines (only save lines with actual data)
      // Deleted lines should be included with isDeleted: 'true'
      const crInvoiceLine = creditNote.creditNoteLines
        ?.map((line, originalIndex) => {
          const isDeleted = deletedRowIndices.has(originalIndex)
          if (
            !isDeleted &&
            (!line.description?.trim() || !line.qty || line.qty <= 0 || !line.taxRateId || line.taxRateId <= 0)
          ) {
            return null
          }
          
          const finalLine = calculateLineTotals(line)
          return {
            lineId: finalLine.lineId || 0,
            invLineId: finalLine.invLineId ?? 0,
            creditNoteId: creditNote.creditNoteId || 0,
            itemId: finalLine.itemId || 0,
            description: finalLine.description || '',
            qty: finalLine.qty || 0,
            unitPrice: finalLine.unitPrice || 0,
            itemPrice: finalLine.itemPrice || 0,
            discountPercent: finalLine.discountPercent || 0,
            discountAmount: finalLine.discountAmount || 0,
            taxRateId: finalLine.taxRateId || 0,
            taxAmount: finalLine.taxAmount || 0,
            lineAmount: finalLine.lineAmount || 0,
            isDeleted: isDeleted ? 'true' : 'false', // Lowercase "true" or "false" (small letters)
          }
        })
        .filter((line): line is NonNullable<typeof line> => line !== null) || []

      // Transform to API structure
      const creditNoteToSave = {
        creditNoteId: creditNote.creditNoteId || 0,
        creditNoteDate: calendarToApi(creditNote.creditNoteDate),
        invoiceId: creditNote.refInvId || 0,
        subTotal: creditNote.subTotal || 0,
        totalTax: creditNote.totalTax || 0,
        totalDiscount: creditNote.totalDiscount || 0,
        total: creditNote.total || 0,
        isFullyRefund: 'false',
        creditNoteType: 1,
        note: creditNote.note || '',
        ipAddress: ipAddress,
        crInvoiceLine: crInvoiceLine,
      }

      // Log the data structure being sent
      console.log('=== Credit Note Save Data Structure ===')
      console.log('Mode:', isEditMode ? 'UPDATE' : 'CREATE')
      console.log('API Payload:', JSON.stringify(creditNoteToSave, null, 2))
      console.log('======================================')

      if (isEditMode) {
        await creditNotesService.updateCreditNote(creditNoteToSave)
      } else {
        await creditNotesService.insertCreditNote(creditNoteToSave)
      }

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(
        isEditMode,
        isEditMode ? t('creditNotes.creditNoteUpdatedSuccess') : t('creditNotes.creditNoteCreatedSuccess')
      )
      
      navigate('/credit-notes')
    } catch (error: any) {
      console.error('Failed to save credit note', error)
      console.error('Error response:', error.response?.data)
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, isEditMode)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = useMemo(() => {
    const options = customers.map((customer) => ({
      value: String(customer.customerId),
      label: customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`,
    }))
    return options
  }, [customers])

  const taxRateOptions: SearchableSelectOption[] = useMemo(() => {
    const options = taxRates.map((tax) => ({
      value: String(tax.taxRateId),
      label: `${tax.taxNameEn || tax.taxNameAr || 'Tax'} (${tax.taxPercent || tax.taxRate || 0}%)`,
    }))
    console.log('Tax rate options:', options.length)
    return options
  }, [taxRates])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!branchId || !selectedCustomerId || selectedCustomerId === 0) {
      invoiceLoadSeq.current += 1
      setInvoices([])
      setInvoicesLoading(false)
      return
    }

    const seq = ++invoiceLoadSeq.current
    setInvoicesLoading(true)
    const loadInvoices = async () => {
      try {
        const excludeId = isEditMode ? Number(id) || 0 : 0
        const invoicesData = await creditNotesService.getAllInvoices(branchId, selectedCustomerId, undefined, excludeId)
        if (seq !== invoiceLoadSeq.current) return
        setInvoices(invoicesData || [])
      } catch (error: any) {
        console.error('Failed to load invoices for customer', error)
        if (seq !== invoiceLoadSeq.current) return
        setInvoices([])
      } finally {
        if (seq === invoiceLoadSeq.current) {
          setInvoicesLoading(false)
        }
      }
    }

    loadInvoices()
  }, [branchId, selectedCustomerId, isEditMode, id])

  // After customer reload, keep the invoice only if it is still eligible
  useEffect(() => {
    if (invoicesLoading) return
    if (!creditNote.refInvId) return
    if (invoices.length === 0) return

    const selectedInv = invoices.find((inv) => getInvoiceId(inv) === creditNote.refInvId)
    if (!selectedInv) return

    const max = Number(selectedInv.maxCreditAmount ?? 0)
    const invNo = selectedInv.invoiceNumber || selectedInv.invoiceNo || ''
    const supply = selectedInv.supplyDate || ''
    setCreditNote((prev) => {
      if (prev.remainingAmount === max && prev.refInvNumber === invNo && (prev.supplyDate || '') === (supply || prev.supplyDate || '')) {
        return prev
      }
      return {
        ...prev,
        remainingAmount: Number.isFinite(max) && max > 0 ? max : prev.remainingAmount,
        refInvNumber: invNo || prev.refInvNumber,
        supplyDate: supply || prev.supplyDate,
      }
    })
  }, [invoices, invoicesLoading, creditNote.refInvId])

  useEffect(() => {
    if (isEditMode) return
    if (!branchId || !creditNote.refInvId) {
      linesLoadSeq.current += 1
      setLinesLoading(false)
      setCreditNote((prev) => {
        if (!prev.creditNoteLines?.length) return prev
        return { ...prev, creditNoteLines: [] }
      })
      setInvoiceLines([])
      setOriginalInvoiceLines(new Map())
      return
    }

    const load = async () => {
      await loadInvoiceSourceLines(creditNote.refInvId!)
    }
    void load()
  }, [branchId, creditNote.refInvId, isEditMode, loadInvoiceSourceLines])

  const availableInvoices = useMemo(() => {
    if (isEditMode) {
      return invoices
    }
    return invoices.filter((inv) => Number(inv.maxCreditAmount ?? inv.unpaidAmount ?? inv.refundablePaidAmount ?? 0) > 0)
  }, [invoices, isEditMode])

  const invoiceOptions: SearchableSelectOption[] = useMemo(() => {
    const seen = new Set<string>(['0'])
    const options: SearchableSelectOption[] = [{ value: '0', label: 'None' }]
    availableInvoices.forEach((inv) => {
      const id = String(getInvoiceId(inv))
      if (!id || id === '0' || seen.has(id)) return
      seen.add(id)
      options.push({
        value: id,
        label: inv.invoiceNumber || inv.invoiceNo || `Invoice ${id}`,
      })
    })
    if (creditNote.refInvId) {
      const currentId = String(creditNote.refInvId)
      if (!seen.has(currentId)) {
        options.push({
          value: currentId,
          label: creditNote.refInvNumber || `Invoice ${currentId}`,
        })
      }
    }
    return options
  }, [availableInvoices, creditNote.refInvId, creditNote.refInvNumber])


  if (loading && !creditNote.creditNoteLines?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Sales Credit Notes"
        description={isEditMode ? t('creditNotes.updateCreditNoteDetails') : t('creditNotes.createNewCreditNote')}
      />

      {/* Client Details card - completely removed as customer name is shown in Credit Note section */}

      <Card>
        <CardHeader>
          <CardTitle>{t('creditNotes.creditNote')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label>{t('creditNotes.creditNoteDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.creditNoteDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, creditNoteDate: e.target.value }))}
              />
            </div>
            {isEditMode && (
              <div className="space-y-2">
                <Label>{t('creditNotes.creditNoteNumberFull')}</Label>
                <Input value={creditNote.creditNoteNumber || ''} readOnly className="bg-muted" placeholder={t('creditNotes.autoGenerated')} />
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('creditNotes.customer') || t('common.customer')}</Label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId ? String(selectedCustomerId) : '0'}
                onValueChange={(value) => {
                  const customerId = Number(value) || 0
                  setSelectedCustomerId(customerId)
                  setInvoiceLines([])
                  setOriginalInvoiceLines(new Map())
                  setCreditNote((prev) => ({
                    ...prev,
                    customerId: customerId,
                    refInvId: 0,
                    refInvNumber: '',
                    remainingAmount: 0,
                    supplyDate: '',
                  }))
                }}
                placeholder={t('creditNotes.selectCustomer')}
                searchPlaceholder={t('creditNotes.searchCustomers')}
                emptyMessage={t('creditNotes.noCustomersFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference Invoice #</Label>
              <SearchableSelect
                key={`invoice-${selectedCustomerId}`}
                options={invoiceOptions}
                value={creditNote.refInvId ? String(creditNote.refInvId) : '0'}
                disabled={!selectedCustomerId || selectedCustomerId === 0 || invoicesLoading}
                onValueChange={(value) => {
                  const invId = Number(value) || 0
                  const selectedInv = availableInvoices.find(inv => getInvoiceId(inv) === invId)

                  if (invId === 0) {
                    setCreditNote((prev) => ({
                      ...prev,
                      refInvId: 0,
                      refInvNumber: '',
                      remainingAmount: 0,
                      supplyDate: '',
                    }))
                    setInvoiceLines([])
                    setOriginalInvoiceLines(new Map())
                    return
                  }

                  setCreditNote((prev) => ({
                    ...prev,
                    refInvId: invId,
                    refInvNumber: selectedInv?.invoiceNumber || selectedInv?.invoiceNo || '',
                    remainingAmount:
                      Number(selectedInv?.maxCreditAmount ?? selectedInv?.unpaidAmount ?? selectedInv?.refundablePaidAmount ?? 0) || 0,
                    customerId: selectedInv?.customerId || prev.customerId,
                    supplyDate: selectedInv?.supplyDate || prev.supplyDate,
                  }))
                }}
                placeholder={t('creditNotes.selectInvoice')}
                searchPlaceholder={t('creditNotes.searchInvoices')}
                emptyMessage={t('creditNotes.noInvoicesFound')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.supplyDate')}</Label>
              <Input
                type="date"
                value={formatDateForInput(creditNote.supplyDate)}
                onChange={(e) => setCreditNote((prev) => ({ ...prev, supplyDate: e.target.value }))}
                readOnly={(creditNote.refInvId ?? 0) > 0}
                className={(creditNote.refInvId ?? 0) > 0 ? 'bg-muted' : ''}
                title={(creditNote.refInvId ?? 0) > 0 ? 'Supply date is set from invoice' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('creditNotes.remainingAmount') || 'Remaining Amount'}</Label>
              <Input
                type="number"
                step="0.01"
                value={creditNote.remainingAmount || ''}
                readOnly
                className="bg-muted"
                placeholder="0.00"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardHeader className="bg-primary/5">
          <div className="space-y-1">
            <CardTitle>{t('creditNotes.creditNoteLines')}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t('creditNotes.linesCannotBeAdded') || 'Lines come from the invoice. New lines cannot be added.'}
            </p>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-primary/5">
                  <th className="text-left p-3 font-semibold align-middle">DESCRIPTION <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">QTY <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">UNIT PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">ITEM PRICE</th>
                  <th className="text-left p-3 font-semibold align-middle">TAXABLE</th>
                  <th className="text-left p-3 font-semibold align-middle">VAT <span className="text-red-500">*</span></th>
                  <th className="text-left p-3 font-semibold align-middle">VAT AMOUNT</th>
                  <th className="text-left p-3 font-semibold align-middle">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {linesLoading && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                      {t('common.loading') || 'Loading...'}
                    </td>
                  </tr>
                )}
                {!linesLoading && (!creditNote.creditNoteLines || creditNote.creditNoteLines.length === 0) && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-muted-foreground">
                      {creditNote.refInvId
                        ? t('creditNotes.noRemainingInvoiceLines') || 'No remaining invoice items to credit.'
                        : t('creditNotes.selectInvoiceToLoadLines') || 'Select an invoice to load its items.'}
                    </td>
                  </tr>
                )}
                {!linesLoading && creditNote.creditNoteLines?.map((line, index) => {
                  const isDeleted = deletedRowIndices.has(index)
                  return (
                    <tr
                      key={index}
                      className={`border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50 ${
                        isDeleted ? 'bg-red-50 dark:bg-red-900/20' : ''
                      }`}
                    >
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            value={line.description || ''}
                            placeholder="Description"
                            disabled
                            className="min-w-[200px] bg-muted"
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            value={line.qty || ''}
                            onChange={(e) => handleLineChange(index, 'qty', Number(e.target.value) || 0)}
                            placeholder="0"
                            disabled={saving || isDeleted || line.qtyEditable === false}
                            className={`w-20 ${line.qtyEditable === false ? 'bg-muted' : ''}`}
                            max={line.maxQty ?? (line.invLineId && line.invLineId > 0 ? originalInvoiceLines.get(line.invLineId)?.maxQty : undefined)}
                            title={line.maxQty != null ? `Max: ${line.maxQty}` : ''}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <Input
                            type="number"
                            step="0.01"
                            value={line.unitPrice || ''}
                            onChange={(e) => handleLineChange(index, 'unitPrice', Number(e.target.value) || 0)}
                            placeholder="0.00"
                            disabled={saving || isDeleted || line.unitPriceEditable === false}
                            className={`w-24 ${line.unitPriceEditable === false ? 'bg-muted' : ''}`}
                            max={line.maxUnitPrice ?? (line.invLineId && line.invLineId > 0 ? originalInvoiceLines.get(line.invLineId)?.maxUnitPrice : undefined)}
                            title={line.maxUnitPrice != null ? `Max: ${line.maxUnitPrice.toFixed(2)}` : ''}
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.itemPrice?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxableAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center h-10">
                          <SearchableSelect
                            options={taxRateOptions}
                            value={line.taxRateId ? String(line.taxRateId) : ''}
                            onValueChange={(value) => handleLineChange(index, 'taxRateId', Number(value))}
                            placeholder="Select VAT"
                            searchPlaceholder="Search VAT..."
                            emptyMessage="No VAT rates found"
                            disabled
                          />
                        </div>
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.taxAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                      <td className="p-3 align-middle">
                        <Input type="number" step="0.01" value={line.lineAmount?.toFixed(2) || '0.00'} readOnly className="bg-muted w-24" />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-primary/5 p-4 rounded-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.subTotal.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL VAT</Label>
                <p className="text-lg font-semibold">{summaryTotals.totalTax.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">TOTAL</Label>
                <p className="text-lg font-semibold">{summaryTotals.total.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes / Remarks</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={creditNote.note || ''}
            onChange={(e) => setCreditNote((prev) => ({ ...prev, note: e.target.value }))}
            placeholder="Enter notes or remarks..."
            className="min-h-[100px]"
          />
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button type="button" variant="outline" onClick={() => navigate('/credit-notes')} className="w-full sm:w-auto">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !branchId || !isFormValid}
          className="w-full sm:w-auto min-w-[150px]"
          title={!isFormValid ? t('creditNotes.pleaseFillAllRequiredFields') : ''}
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('creditNotes.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {isEditMode ? t('creditNotes.updateCreditNote') : t('creditNotes.saveCreditNote')}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

